/**
 * Authoring checks for a form template — the problems that would reach
 * applicants if the template were published as it stands.
 *
 * Shared by the builder canvas (which flags them per-question) and the form
 * editor (which blocks Activate on them). One definition so the warning you see
 * and the rule that stops you publishing can never disagree.
 *
 * These are AUTHORING checks, deliberately separate from `validateFormSchema`:
 * that validates the JSON shape and is enforced server-side, whereas a dropdown
 * with zero options is structurally valid and merely useless.
 */
import type { FieldOption, FieldType, FormField } from '@clubezy/form-builder';

/** Field types whose options the builder can edit. */
const CHOICE_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  'select',
  'radio',
  'checkbox',
]);

/** A problem found on one question. */
export type FormIssue = {
  /** The control this is about. */
  fieldId: string;
  /** The question's wording, for listing issues away from the field itself. */
  fieldLabel: string;
  /** What is wrong, phrased for the person who has to fix it. */
  message: string;
};

/** True when this control draws its options from club data rather than a list. */
function hasDynamicOptions(field: FormField): boolean {
  const source = (field as unknown as Record<string, unknown>)['optionsSource'];
  return typeof source === 'string' && source.length > 0;
}

function optionsOf(field: FormField): FieldOption[] {
  const value = (field as unknown as Record<string, unknown>)['options'];
  return Array.isArray(value) ? (value as FieldOption[]) : [];
}

/** True when a number field's Max is below its Min, so nothing is accepted. */
export function boundsInverted(field: FormField): boolean {
  const { min, max } = field.validation ?? {};
  return typeof min === 'number' && typeof max === 'number' && max < min;
}

/**
 * The first problem with a single question, or null when it's fine. Used for
 * the "Needs attention" chip on the collapsed row.
 */
export function fieldIssue(field: FormField): string | null {
  if (!field.label?.trim()) {
    return 'This question has no wording yet.';
  }
  if (
    CHOICE_TYPES.has(field.type) &&
    !hasDynamicOptions(field) &&
    optionsOf(field).length === 0
  ) {
    return 'This question has no options, so nobody can answer it.';
  }
  if (boundsInverted(field)) {
    return 'Max is below Min, so no answer will be accepted.';
  }
  return null;
}

/**
 * Every problem across a template, walking nested groups and repeatable rows.
 * An empty array means the template is safe to publish.
 */
export function formIssues(controls: readonly FormField[] | null | undefined): FormIssue[] {
  const issues: FormIssue[] = [];
  const walk = (fields: readonly FormField[], depth: number): void => {
    if (depth > 5) return;
    for (const field of fields) {
      if (!field || typeof field !== 'object') continue;
      const message = fieldIssue(field);
      if (message) {
        issues.push({
          fieldId: field.id,
          fieldLabel: field.label?.trim() || 'Untitled question',
          message,
        });
      }
      const nested = field as unknown as Record<string, unknown>;
      for (const key of ['children', 'itemTemplate']) {
        const children = nested[key];
        if (Array.isArray(children)) walk(children as FormField[], depth + 1);
      }
    }
  };
  if (Array.isArray(controls)) walk(controls, 0);
  return issues;
}
